/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import groupBy from 'lodash.groupby';
import isNil from 'lodash.isnil';
import {
  cirusPrimaryProperties,
  DICKEY_JOHN_CHANNEL_ATTRIBUTES,
  dickeyJohnPrimaryProperties,
  dickeyJohnSensorProperties,
  forceAmericaDisplayProperties,
  GenericObject,
  spreaderProperties
} from '../types/entities';
import {isObjectEmpty} from '../utils/ObjectUtil';
import {
  isCirus,
  isDickeyJohn,
  isForceAmerica,
  isRoadWatch
} from './Manufacturer';

export enum SpreaderOptions {
  CIRRUS = 'Cirrus',
  DICKEYJOHN = 'Dicky John',
  FORCEAMERICA = 'Force America'
};
/**
 * Function to transform Sensor Data.
 * Each sensor types of Dickey John, Force America and Cirus will use this
 * function to capture values.
 * Capture sensor type and the sensor measures.
 *
 * ```typescript
 *  import {transformSpreaderData} from '@ion/core/src/entities';
 *  transformSpreaderData(sensorData);
 * ```
 */
export const transformSpreaderData = (data: any[]) => {
  const {device, messageTime, sensorType} = data?.[0]?.sensorData || {};
  if (sensorType) {
    const message = {
      sensorType,
      messageTime,
      device,
      measures : {}
    };
    const results = data?.reduce((acc, item, index) => {
      const {data: sensorData} = item?.sensorData;
      if (sensorData?.length) {
        if (isCirus(sensorType) || isRoadWatch(sensorType) || isForceAmerica(sensorType)) {
          const transformer = isForceAmerica(sensorType) ? transformForceAmericaSpreaderData
            : transformCirusSpreaderData;
          const transformedData = transformer({data: sensorData});
          if (!isObjectEmpty(acc)) {
            const existingKeys = Object.keys(acc);
            const filteredKeys = Object.keys(transformedData).filter((key: string) => !existingKeys.includes(key));
            filteredKeys?.forEach((key) => {
              acc[key] = transformedData[key];
            });
            return index === data?.length - 1 ? {
              ...message,
              measures : acc
            }: acc;
          }
          return {...transformedData};
        }
        if (isDickeyJohn(sensorType)) {
          const transformedData: {[key: string]: any} = transformDickeyJohnSpreaderData({
            sensorType,
            data         : sensorData,
            initialItems : acc[sensorType]?.measures ?? {}
          });
          const currentKeys = Object.keys(transformedData || {}).filter((key) => {
            return transformedData[key]?.length && isNil(acc[key]);
          });
          currentKeys?.length && currentKeys.forEach((key) => {
            acc[key] = transformedData[key];
          });
        }
      }
      return acc;
    }, {});
    if (isDickeyJohn(sensorType)) {
      return {...message, measures: groupData(results, message.sensorType)};
    }
    return results;
  }
  return {};
};

/**
 * Function to transform Dickey John messages.
 * It will fetch data from multiple messages for granular and liquid types.
 */
const transformDickeyJohnSpreaderData = ({data, initialItems, sensorType}) => {
  data?.forEach((item) => {
    const {details} = item;
    const unitType = details?.find((obj) => obj?.label === 'cASubElementId');
    if (unitType?.value && spreaderProperties[sensorType]?.includes(unitType?.value)) {
      const items = details
        ?.filter(({label}: {label: string; value: string}) => dickeyJohnSensorProperties.includes(label))
        ?.reduce((acc, {label, value}) => {
          const preferredLabel = label.split('sensor')[1].toLowerCase();
          acc[preferredLabel] = value?.trim?.();
          return acc;
        }, {});
      isNil(initialItems[unitType?.value]) && (initialItems[unitType?.value] = []);
      initialItems[unitType?.value].push(items);
    }
    if (!unitType?.value) {
      const keys = Object.keys(dickeyJohnPrimaryProperties);
      const items = details
        .filter(({label}: {label: string; value: string}) => keys.includes(label))
        .reduce((acc, {label, value}) => {
          acc[dickeyJohnPrimaryProperties[label]] = value;
          return acc;
        }, {});
      isNil(initialItems.flowRate) && (initialItems.flowRate = []);
      if (DICKEY_JOHN_CHANNEL_ATTRIBUTES.includes(Object.keys(items)[0])) {
        const channelValue = {
          type    : Object.keys(items)[0],
          value   : Object.values(items)[0],
          channel : details.find(item => item?.label === 'messageGroup')?.value
        };
        !isObjectEmpty(items) && initialItems.flowRate.push(channelValue);
      } else {
        !isObjectEmpty(items) && initialItems.flowRate.push(items);
      }
    }
  });
  return initialItems;
};

const groupData = (data, sensorType) => {
  if (data) {
    const groupedResults = Object?.keys(data).reduce((acc, key) => {
      const groupedKey = spreaderProperties[sensorType]?.includes(key)
        ? groupBy(data[key], 'setting')
        : data[key];
      acc[key] = groupedKey;
      return acc;
    }, {});
    return groupedResults;
  }
  return {};
};

/**
 * parseForceAmericaMessage function will parse the Force America messages.
 *
 * @param message string message that is msg.ATTRIBUTE
 * @returns attribute string without the prefix of msg.
 */
const parseForceAmericaMessage = (message: string) => message?.slice(4);

/**
 * filterForceAmericaData filters the data from the message based on the keys given.
 *
 * const transformedData = filterForceAmericaData(data)
 *
 * @param data array of sensor data
 * @returns an object of filtered data
 */
export const filterForceAmericaData = (data) => {
  if (!isNil(data)) {
    const keys = Object.keys(forceAmericaDisplayProperties);
    const filteredItems = data
      ?.filter(({label}: {label: string; value: string}) =>
        keys.includes(parseForceAmericaMessage(label)))
      ?.reduce((acc, {label, value}) => {
        acc[forceAmericaDisplayProperties[parseForceAmericaMessage(label)]] = value;
        return acc;
      }, {});
    return filteredItems;
  }
  return {};
};

export const transformForceAmericaSpreaderData = ({data}) => {
  const flattenedData = flattenSpreaderData(data);
  const transformedData = filterForceAmericaData(flattenedData);
  return transformedData;
};

/**
 * filterCirusData filters the data from the message based on the keys given.
 *
 * const transformedData = filterCirusData(data)
 *
 * @param data array of sensor data
 * @returns an object of filtered data
 */
export const filterCirusData = (data) => {
  if (!isNil(data)) {
    const keys = Object.keys(cirusPrimaryProperties);
    const filteredItems = data
      ?.filter(({label}: {label: string; value: string}) =>
        keys.includes(label))
      ?.reduce((acc, {label, value}) => {
        acc[cirusPrimaryProperties[label]] = value;
        return acc;
      }, {});
    return filteredItems;
  }
  return {};
};

/**
 * @private
 *
 * flattenSpreaderData is a function that is used to flatten the data for a spreader.
 *
 * ```typescript
 * import {flattenSpreaderData} from '@ion/ion-suite/src/manufacturer/spreaderUtils';
 *
 * const transformCirusSpreaderData = ({data}) => {
 *   const flattenedData = flattenSpreaderData(data);
 *   const transformedData = filterSpreaderData(flattenedData);
 *   return transformedData;
 * };
 * ```
 * @param array
 * @returns a flattened array of spreader data
 */
const flattenSpreaderData = (array: GenericObject[]) => {
  const data = array.reduce((acc, curr) => {
    acc.push(curr?.details);
    return acc;
  }, []);
  return data.flat();
};

/**
 * transformCirusSpreaderData is a function that is used to transform the data for a Cirus spreader.
 *
 * ```typescript
 * import {transformCirusSpreaderData} from '@ion/ion-suite/src/manufacturer/spreaderUtils';
 *
 * if (isCirus(sensorType)) {
 *   const transformedData = transformCirusSpreaderData({data: sensorData});
 *   ...
 *   return transformedData;
 * }
 * ```
 */
export const transformCirusSpreaderData = ({data}) => {
  const flattenedData = flattenSpreaderData(data) || {};
  const transformedData = filterCirusData(flattenedData);
  return transformedData;
};
